import { PrimaryButton } from '@components/Buttons/PrimaryButton';
import { ModalWindow } from '@components/Modal/ModalWindow';
import { ModalWindowActions } from '@components/Modal/ModalWindowActions';
import { ModalWindowContent } from '@components/Modal/ModalWindowContent';
import { ModalWindowTitle } from '@components/Modal/ModalWindowTitle';
import { useField } from '@hooks/useField';
import { useForm } from '@hooks/useForm';
import useLoading from '@hooks/useLoading';
import useMessage from '@hooks/useMessage';
import { IModalWindow } from '@hooks/useModalWindow';
import { useValidation } from '@hooks/useValidation';
import { IUsuario } from '@model/Usuario';
import { useUsuarioService } from '@services/UsuarioService';
import { yup } from '@validations/Yup';
import { AxiosResponse } from 'axios';
import React from 'react';
import { UsuarioRegistrarForm } from './UsuarioRegistrarForm';

export type UsuarioRegistrarModalProps = {
    modal: IModalWindow
    onUsuarioRegistrado: (usuario: AxiosResponse<IUsuario>) => void
}

export function UsuarioRegistrarModal ({ modal, onUsuarioRegistrado }: UsuarioRegistrarModalProps) {
    const loading = useLoading();
    const message = useMessage();

    const form = useForm<Partial<IUsuario>>({
        nome: useField(),
        nomeUsuario: useField(),
        senha: useField()
    })

    const validation = useValidation<IUsuario>({
        nome: yup.string().trim().required(),
        nomeUsuario: yup.string().trim().required(),
        senha: yup.string().trim().required().min(6)
    });

    const usuarioService = useUsuarioService();

    const handleConfirm = React.useCallback(() => {
        if (!loading.isLoading) {
            const values = form.getInputValues();

            form.clearErrors();

            validation.validate(values)
                .then(() => {
                    loading.start();
                    usuarioService.registrar(values)
                        .then(response => {
                            message.success("Usuário criado com sucesso");
                            modal.close();
                            onUsuarioRegistrado(response);
                        })
                        .catch(e => {
                            message.handleResponse(e)
                                .badRequest(form.setErrorsFromServer)
                                .showException();
                        })
                        .finally(loading.stop);
                })
                .catch(form.setErrorsFromYup);
        }
    }, [form, loading, message, modal, onUsuarioRegistrado, usuarioService, validation]);

    const handleEnter = React.useCallback(() => {
        form.reset();
    }, [form]);

    return (
        <ModalWindow
            modal={modal}
            disabled={loading.isLoading}
            size="md"
            TransitionProps={{
                onEnter: handleEnter
            }}
        >
            <ModalWindowTitle 
                title={`Registro de Novo Usuário`}
            />

            <ModalWindowContent>
                <UsuarioRegistrarForm 
                    form={form}
                />
            </ModalWindowContent>

            <ModalWindowActions>
                <PrimaryButton onClick={handleConfirm}>Registrar Usuário</PrimaryButton>
            </ModalWindowActions>
        </ModalWindow>
    )
}